import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, 
  MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography  
} from "@mui/material";
import { fetchWithAuth } from 'utils/fetchWithAuth';

function PaiementModal({ location, lot, onClose, refreshLot, selectedMonth }) {
  const [montant, setMontant] = useState(location.loyer || "");
  const [modes, setModes] = useState([]);
  const [moyen, setMoyen] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [historiqueLoyers, setHistoriqueLoyers] = useState([]);
  const [moisSelectable, setMoisSelectable] = useState([]);
  const [moisConcerne, setMoisConcerne] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/loyers/historique/${location.id}/`)
      .then(res => res.json())
      .then(data => {
        const sixDerniers = data.slice(0, 6) || [];
        setHistoriqueLoyers(sixDerniers);

        const enRetardOuDus = sixDerniers.filter(
          loyer => ["dû", "en retard"].includes(loyer.etat)
        );
        setMoisSelectable(enRetardOuDus);
        if (enRetardOuDus.length) {
          setMoisConcerne(enRetardOuDus[enRetardOuDus.length - 1].mois);
        } else {
          setMoisConcerne("");
        }    
      });
  }, [API_URL, location.id]);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/paiement/modes/`)
      .then(resp => resp.json())
      .then(data => {
        setModes(data.modes ?? []);
        setMoyen(data.default ?? "");
      });
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!Number(montant) || Number(montant) < 0) {
      setResult("Montant invalide");
      setLoading(false);
      return;
    }
    if (!moyen) {
      setResult("Sélectionner un moyen de paiement");
      setLoading(false);
      return;
    }
    try {
      const resp = await fetchWithAuth(`${API_URL}/api/paiement/${location.id}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          montant,
          moyen,
          mois_concerne: moisConcerne 
        }),
      });
      if (resp.ok) {
        setResult("Paiement enregistré !");
        await refreshLot();
        setTimeout(onClose, 1500);
      } else setResult("Erreur : paiement");
    } catch (e) {
      setResult("Erreur réseau");
    }
    setLoading(false);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle>Enregistrer un paiement</DialogTitle>

      <DialogContent sx={{ flex: 1, overflowY: "auto", pb: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Locataire :</strong> {location.locataire_referent.prenom}{" "}
          {location.locataire_referent.nom}
          <br />
          <strong>Lot :</strong> {lot.nom}
          <br />
          <strong>Adresse :</strong> {lot.bien.adresse}
          <br />
          <strong>SCI :</strong> {lot.bien.sci.raison_sociale}
        </Typography>

        <Table size="small" sx={{ mt: 1, mb: 5  }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f7fafb" }}>
              <TableCell sx={{ fontWeight: 700 }}>Mois</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Montant</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>État</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historiqueLoyers.map((l, i) => (
              <TableRow key={i}>
                <TableCell>
                  {new Date(l.mois).toLocaleDateString("fr-FR", {
                    month: "long",
                    year: "numeric"
                  })}
                </TableCell>
                <TableCell>{l.montant} €</TableCell>
                <TableCell>{l.etat}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Montant (€)"
            type="number"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            fullWidth
            required
          />

          <TextField
            select
            label="Moyen de paiement"
            value={moyen}
            onChange={(e) => setMoyen(e.target.value)}
            fullWidth
            required
          >
            {modes.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                {mode.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Mois concerné"
            value={moisConcerne}
            onChange={(e) => setMoisConcerne(e.target.value)}
            fullWidth
            required
          >
            <MenuItem value="">-- Sélectionner un mois --</MenuItem>
            {moisSelectable.map((l) => (
              <MenuItem key={l.mois} value={l.mois}>
                {new Date(l.mois).toLocaleDateString("fr-FR", {
                  month: "long",
                  year: "numeric"
                })}
              </MenuItem>
            ))}
          </TextField>

          {result && (
            <Alert
              severity={result.includes("Erreur") ? "error" : "success"}
              sx={{ mt: 2, mb: 1 }}
            >
              {result}
            </Alert>
          )}
          <DialogActions sx={{ px: 0 }}>
            <Button onClick={onClose} color="inherit">
              Annuler
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              Valider
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default PaiementModal;